// AdminViewAsPrompt - Prompt shown to admin users on portal inbox
// suggesting they switch to a user account for better experience.

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authService, type ViewAsUser } from '../../services/auth';
import styles from './AdminViewAsPrompt.module.css';

/**
 * When an admin visits the portal inbox without impersonating,
 * this dialog suggests switching to a user account.
 *
 * Phase 1: confirmation prompt (yes/no)
 * Phase 2: user picker (reuses listUsersForViewAs from auth service)
 */
export default function AdminViewAsPrompt() {
  const { user, isImpersonating, impersonate } = useAuth();

  const [dismissed, setDismissed] = useState(false);
  const [phase, setPhase] = useState<'prompt' | 'pick'>('prompt');
  const [users, setUsers] = useState<ViewAsUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  const isAdmin = user?.role === 'ADMIN';

  // Don't show if not admin, already impersonating, or dismissed
  if (!isAdmin || isImpersonating || dismissed) return null;

  const handleYes = async () => {
    setPhase('pick');
    setLoading(true);
    setError(null);
    try {
      const result = await authService.listUsersForViewAs('portal:read');
      setUsers(result);
    } catch {
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (userId: string) => {
    setSwitching(true);
    try {
      await impersonate(userId);
      // Page will re-render with impersonated user context
    } catch {
      setError('Failed to switch user. Please try again.');
      setSwitching(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        {phase === 'prompt' && (
          <>
            <div className={styles.iconRow}>
              <div className={styles.icon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
            </div>
            <h2 className={styles.title}>Viewing as Admin</h2>
            <p className={styles.message}>
              You&apos;re viewing this page as <strong>admin</strong> for testing
              purposes. This page doesn&apos;t work well for the admin account.
              We recommend viewing it as a user.
            </p>
            <p className={styles.question}>Would you like to switch to a user account?</p>
            <div className={styles.actions}>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleYes}
                type="button"
                data-action="inbox_view_as_accept"
                data-outcome="open"
              >
                Yes, show me the options
              </button>
              <button
                className={`${styles.btn} ${styles.btnOutline}`}
                onClick={() => setDismissed(true)}
                type="button"
                data-action="inbox_view_as_decline"
                data-outcome="close"
              >
                No, continue as admin
              </button>
            </div>
          </>
        )}

        {phase === 'pick' && (
          <>
            <div className={styles.header}>
              <h2 className={styles.title}>Select a User</h2>
              <button
                className={styles.closeBtn}
                onClick={() => setDismissed(true)}
                type="button"
                aria-label="Close"
                data-action="inbox_view_as_close"
                data-outcome="close"
              >
                &times;
              </button>
            </div>
            <p className={styles.pickSubtext}>
              Choose a user account to view the inbox as:
            </p>

            {loading && (
              <div className={styles.loadingState}>Loading users...</div>
            )}
            {error && <div className={styles.errorState}>{error}</div>}

            {!loading && !error && users.length === 0 && (
              <div className={styles.emptyState}>No user accounts found.</div>
            )}

            {!loading && !error && users.length > 0 && (
              <div className={styles.userList}>
                {users.map((u) => (
                  <button
                    key={u.id}
                    className={styles.userOption}
                    data-action="inbox_person_pick"
                    data-target-user-id={u.id}
                    onClick={() => handleSelectUser(u.id)}
                    disabled={switching}
                    type="button"
                  >
                    <div className={styles.userAvatar}>
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.userInfo}>
                      <span className={styles.userName}>{u.name}</span>
                      <span className={styles.userMeta}>
                        {u.email}
                        {u.salesperson_id ? ` \u00B7 ${u.salesperson_id}` : ''}
                      </span>
                    </div>
                    <svg className={styles.userArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                ))}
              </div>
            )}

            <div className={styles.footer}>
              <button
                className={`${styles.btn} ${styles.btnOutline}`}
                onClick={() => setDismissed(true)}
                disabled={switching}
                type="button"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
